import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { FaUserPlus, FaSignInAlt, FaKey } from "react-icons/fa";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { useSystemSettings } from "@/hooks/use-system-settings";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { languageManager } from "@/lib/language-manager";
import { useTranslation } from "react-i18next";

type LoginFormData = {
  email: string;
  password: string;
  rememberMe: boolean;
};

// Schema pre formulár zabudnutého hesla
const forgotPasswordSchema = z.object({
  email: z.string().email("Neplatný email")
});

// Schema pre formulár resetovania hesla
const resetPasswordSchema = z.object({
  password: z.string()
    .min(8, "Heslo musí mať aspoň 8 znakov")
    .regex(/[A-Z]/, "Heslo musí obsahovať aspoň jedno veľké písmeno")
    .regex(/[a-z]/, "Heslo musí obsahovať aspoň jedno malé písmeno")
    .regex(/[0-9]/, "Heslo musí obsahovať aspoň jednu číslicu")
    .regex(/[^A-Za-z0-9]/, "Heslo musí obsahovať aspoň jeden špeciálny znak"),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Heslá sa nezhodujú",
  path: ["confirmPassword"]
});

// Definujeme schému lokálne, aby sme sa vyhli problémom s importovanou schémou, ktorá môže vyžadovať 'name'
const registerFormSchema = z.object({
  email: z.string().email("Neplatný email"),
  password: z.string().min(6, "Heslo musí mať aspoň 6 znakov"),
  confirmPassword: z.string().min(6, "Potvrdenie hesla je povinné"),
  businessId: z.string().optional(),
  market: z.enum(["SK", "US"]).default("SK"),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  companyName: z.string().optional()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Heslá sa nezhodujú",
  path: ["confirmPassword"]
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
type RegisterFormData = z.infer<typeof registerFormSchema>;

export default function AuthPage() {
  const { loginMutation, registerMutation, user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { getSettingValue, getImageValue } = useSystemSettings();
  const { i18n } = useTranslation();
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetRequestSent, setResetRequestSent] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  
  // Získame hodnoty nastavení zo systému
  const appTitle = getSettingValue("app.name", "Task Management System");
  const appSubtitle = getSettingValue("auth.welcomeMessage", "Prihláste sa alebo vytvorte nový účet");
  const welcomeTitle = getSettingValue("auth.welcomeTitle", "Vitajte v Task Pro");
  const welcomeDescription = getSettingValue("auth.welcomeDescription", 
    "Spravujte svojich klientov, sledujte úlohy a rozvíjajte svoje podnikanie s naším profesionálnym systémom na správu úloh.");
  const bgImageUrl = getImageValue("login.backgroundImage", "");

  // Načítanie slides z public settings
  const { data: publicSettings = [] } = useQuery({
    queryKey: ['/api/system-settings/public'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/system-settings/public');
      return response.json();
    },
    staleTime: 60000, // 1 minuta
  });

  // Parsovanie slides z public settings
  const slides = useMemo(() => {
    const slideSettings = publicSettings.filter((s: any) => s.key && s.key.startsWith('login.slides.'));
    const slideIds = new Set<string>();
    
    slideSettings.forEach((setting: any) => {
      const match = setting.key.match(/login\.slides\.(.+)\.(.+)/);
      if (match) {
        slideIds.add(match[1]);
      }
    });
    
    const slidesData: Array<{ id: string; image: string; title: string; description: string; order: number }> = [];
    
    // Zistíme aktuálny jazyk
    const currentLanguage = i18n.language || 'sk';
    const langSuffix = currentLanguage.startsWith('en') ? '_en' : '_sk';
    
    slideIds.forEach(id => {
      const getSlideValue = (field: string) => {
        const setting = slideSettings.find((s: any) => s.key === `login.slides.${id}.${field}`);
        if (!setting) return '';
        try {
          const parsed = JSON.parse(setting.value);
          return typeof parsed === 'string' ? parsed : String(parsed);
        } catch {
          return setting.value || '';
        }
      };
      
      const image = getSlideValue('image');
      // Použijeme správnu jazykovú verziu
      const title = getSlideValue(`title${langSuffix}`);
      const description = getSlideValue(`description${langSuffix}`);
      const order = parseInt(getSlideValue('order') || '0');
      
      if (image || title || description) {
        slidesData.push({ id, image, title, description, order });
      }
    });
    
    slidesData.sort((a, b) => a.order - b.order);
    return slidesData;
  }, [publicSettings, i18n.language]);

  // Auto-rotate slides každých 5 sekúnd
  useEffect(() => {
    if (slides.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [slides.length]);

  // Automatická detekcia jazyka podľa IP pri prvom načítaní
  useEffect(() => {
    const detectAndSetLanguage = async () => {
      try {
        // Skontrolujeme, či už nie je jazyk nastavený v localStorage
        const savedLanguage = localStorage.getItem('i18nextLng');
        if (savedLanguage && (savedLanguage === 'sk' || savedLanguage === 'en')) {
          console.log('[GEO] Language already set in localStorage:', savedLanguage);
          return;
        }

        // Zavoláme endpoint pre detekciu jazyka podľa IP
        const response = await fetch('/api/detect-language');
        const data = await response.json();
        
        console.log('[GEO] Detected language:', data);
        
        if (data.language && (data.language === 'sk' || data.language === 'en')) {
          await languageManager.setLanguage(data.language);
          console.log(`[GEO] Language set to ${data.language} based on IP geolocation (${data.country})`);
        }
      } catch (error) {
        console.error('[GEO] Failed to detect language:', error);
        // Fallback na slovenčinu pri chybe
        await languageManager.setLanguage('sk');
      }
    };

    detectAndSetLanguage();
  }, []); // Zavolá sa len raz pri mount

  const loginForm = useForm<LoginFormData>({
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false
    }
  });
  
  const forgotPasswordForm = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: ""
    }
  });
  
  const handleForgotPassword = async (data: ForgotPasswordFormData) => {
    try {
      const response = await fetch('/api/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: data.email }),
      });
      
      if (response.ok) {
        setResetRequestSent(true);
        toast({
          title: 'Email odoslaný',
          description: 'Pokyny na obnovu hesla boli odoslané na vašu emailovú adresu.',
          variant: 'default',
        });
      } else {
        toast({
          title: 'Informácia',
          description: 'Email sa nepodarilo odoslať. Skontrolujte zadanú emailovú adresu.',
          variant: 'default',
        });
      }
    } catch (error) {
      toast({
        title: 'Informácia',
        description: 'Skúste to prosím znovu o chvíľu.',
        variant: 'default',
      });
    }
  };

  const handleResendVerification = async () => {
    const email = (loginForm.getValues("email") || "").trim();
    if (!email) {
      toast({
        title: "Zadajte email",
        description: "Najprv zadajte email, na ktorý chcete odoslať verifikáciu.",
        variant: "default",
      });
      return;
    }

    try {
      const response = await apiRequest("POST", "/api/resend-verification", { email });
      const data = await response.json().catch(() => null);
      const success = data?.success !== false;
      toast({
        title: success ? "Verifikačný email odoslaný" : "Email neodoslaný",
        description: data?.message || "Skontrolujte svoj email a kliknite na overovací odkaz.",
        variant: success ? "default" : "destructive",
      });
    } catch (error: any) {
      toast({
        title: "Chyba pri odoslaní",
        description: error?.message || "Nepodarilo sa odoslať verifikačný email.",
        variant: "destructive",
      });
    }
  };

  const registerForm = useForm<RegisterFormData>({
    resolver: zodResolver(registerFormSchema),
    mode: "onChange", // Validácia pri každej zmene
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      businessId: "",
      market: "SK"
    }
  });

  const registerButtonRef = useRef<HTMLButtonElement>(null);

  // Funkcia pre odoslanie registrácie - MUSÍ BYŤ PRED useEffect, ktorý ju používa
  const handleRegisterSubmit = useCallback(() => {
    console.log('[REGISTER] ========== handleRegisterSubmit CALLED ==========');
    console.log('[REGISTER] Form state:', {
      isValid: registerForm.formState.isValid,
      isDirty: registerForm.formState.isDirty,
      errors: registerForm.formState.errors,
      values: registerForm.getValues(),
      isPending: registerMutation.isPending
    });
    
    if (registerMutation.isPending) {
      console.log('[REGISTER] Already pending, skipping');
      return;
    }
    
    try {
      registerForm.handleSubmit(
        (data) => {
          console.log('[REGISTER] ✅ Form validation PASSED, data:', data);
          const { confirmPassword, businessId, ...registrationData } = data;
          console.log('[REGISTER] Calling registerMutation.mutate with:', registrationData);
          registerMutation.mutate(registrationData, {
            onError: (error: any) => {
              console.log('[REGISTER] Mutation onError callback:', error);
              // Ak je to email exists error, presmeruj na email-exists page
              if (error?.code === 'EMAIL_EXISTS' || error?.message?.includes('už registrovaný')) {
                setLocation(`/email-exists?email=${encodeURIComponent(registrationData.email)}`);
              } else {
                toast({
                  title: "Registrácia zlyhala",
                  description: error?.message || "Nastala chyba pri registrácii",
                  variant: "destructive",
                });
              }
            },
            onSuccess: (data) => {
              console.log('[REGISTER] Mutation onSuccess callback:', data);
              // Presmeruj na registration success page
              setLocation(`/registration-success?email=${encodeURIComponent(registrationData.email)}`);
            }
          });
        },
        (errors) => {
          console.log('[REGISTER] ❌ Form validation FAILED, errors:', JSON.stringify(errors, null, 2));
          console.log('[REGISTER] ❌ Current form values:', registerForm.getValues());
          console.log('[REGISTER] ❌ Form state:', {
            isValid: registerForm.formState.isValid,
            isDirty: registerForm.formState.isDirty,
            touchedFields: registerForm.formState.touchedFields,
            dirtyFields: registerForm.formState.dirtyFields
          });
          
          // Zobraz detailné chyby
          const errorMessages = Object.entries(errors).map(([field, error]: [string, any]) => {
            return `${field}: ${error?.message || JSON.stringify(error)}`;
          }).join(', ');
          
          toast({
            title: "Chyba validácie",
            description: errorMessages || "Prosím vyplňte všetky povinné polia správne.",
            variant: "destructive",
          });
        }
      )();
    } catch (error) {
      console.error('[REGISTER] ERROR in handleRegisterSubmit:', error);
    }
  }, [registerForm, registerMutation, toast]);

  // Debug: loguj zmeny vo formulári
  useEffect(() => {
    const subscription = registerForm.watch((value, { name, type }) => {
      console.log('[REGISTER] Form field changed:', { name, type, value });
    });
    return () => subscription.unsubscribe();
  }, [registerForm.watch]);

  // Priame DOM event listenery - maximálne agresívne obísť React event systém a extensiony
  // Používa MutationObserver pre sledovanie, kedy sa button objaví v DOM (pri prepnutí na registračný tab)
  useEffect(() => {
    console.log('[REGISTER] 🔍 Starting MutationObserver for register button');
    const installedButtons = new WeakSet<HTMLButtonElement>();
    let cleanupFunctions: Array<() => void> = [];

    const setupAggressiveListeners = () => {
      const button = document.getElementById('register-button') as HTMLButtonElement;
      if (!button) {
        return;
      }

      // Ak už má listenery, najprv ich odstráň
      if (installedButtons.has(button)) {
        console.log('[REGISTER] 🔄 Button already has listeners, removing old ones first');
        // Odstráň staré listenery pomocou cleanup funkcií
        cleanupFunctions.forEach(cleanup => cleanup());
        cleanupFunctions = [];
        installedButtons.delete(button);
      }

      console.log('[REGISTER] 🔥 Setting up AGGRESSIVE DOM event listeners');

      const handleMouseDown = (e: MouseEvent) => {
        console.log('[REGISTER] 🔥🔥🔥 AGGRESSIVE mousedown FIRED!', e);
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (!registerMutation.isPending) {
          const form = button.closest('form') as HTMLFormElement;
          if (form) {
            console.log('[REGISTER] 🔥 Submitting form from AGGRESSIVE mousedown');
            form.requestSubmit();
          } else {
            console.log('[REGISTER] 🔥 Form not found, calling handleRegisterSubmit directly');
            handleRegisterSubmit();
          }
        }
        return false;
      };

      const handlePointerDown = (e: PointerEvent) => {
        console.log('[REGISTER] 🔥🔥🔥 AGGRESSIVE pointerdown FIRED!', e);
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (!registerMutation.isPending) {
          const form = button.closest('form') as HTMLFormElement;
          if (form) {
            console.log('[REGISTER] 🔥 Submitting form from AGGRESSIVE pointerdown');
            form.requestSubmit();
          } else {
            console.log('[REGISTER] 🔥 Form not found, calling handleRegisterSubmit directly');
            handleRegisterSubmit();
          }
        }
        return false;
      };

      const handleClick = (e: MouseEvent) => {
        console.log('[REGISTER] 🔥🔥🔥 AGGRESSIVE click FIRED!', e);
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (!registerMutation.isPending) {
          const form = button.closest('form') as HTMLFormElement;
          if (form) {
            console.log('[REGISTER] 🔥 Submitting form from AGGRESSIVE click');
            form.requestSubmit();
          } else {
            console.log('[REGISTER] 🔥 Form not found, calling handleRegisterSubmit directly');
            handleRegisterSubmit();
          }
        }
        return false;
      };

      const handleTouchStart = (e: TouchEvent) => {
        console.log('[REGISTER] 🔥🔥🔥 AGGRESSIVE touchstart FIRED!', e);
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (!registerMutation.isPending) {
          const form = button.closest('form') as HTMLFormElement;
          if (form) {
            console.log('[REGISTER] 🔥 Submitting form from AGGRESSIVE touchstart');
            form.requestSubmit();
          } else {
            console.log('[REGISTER] 🔥 Form not found, calling handleRegisterSubmit directly');
            handleRegisterSubmit();
          }
        }
        return false;
      };

      // Pridaj listenery v capture phase (true = capture) - najvyššia priorita
      button.addEventListener('mousedown', handleMouseDown, { capture: true, passive: false });
      button.addEventListener('pointerdown', handlePointerDown, { capture: true, passive: false });
      button.addEventListener('click', handleClick, { capture: true, passive: false });
      button.addEventListener('touchstart', handleTouchStart, { capture: true, passive: false });

      // Pridaj aj ako priame atribúty (prepíšu akékoľvek existujúce)
      (button as any).onclick = handleClick;
      (button as any).onmousedown = handleMouseDown;
      (button as any).onpointerdown = handlePointerDown;
      (button as any).ontouchstart = handleTouchStart;

      // Označ button ako inštalovaný
      installedButtons.add(button);
      console.log('[REGISTER] ✅ AGGRESSIVE listeners installed successfully');

      // Vytvor cleanup funkciu pre tento button
      const cleanup = () => {
        button.removeEventListener('mousedown', handleMouseDown, true);
        button.removeEventListener('pointerdown', handlePointerDown, true);
        button.removeEventListener('click', handleClick, true);
        button.removeEventListener('touchstart', handleTouchStart, true);
        // Odstráň priame atribúty
        (button as any).onclick = null;
        (button as any).onmousedown = null;
        (button as any).onpointerdown = null;
        (button as any).ontouchstart = null;
        installedButtons.delete(button);
      };
      
      cleanupFunctions.push(cleanup);
    };

    // Skús hneď
    setupAggressiveListeners();

    // Pridaj MutationObserver na sledovanie, kedy sa button objaví v DOM
    const observer = new MutationObserver((mutations) => {
      setupAggressiveListeners();
    });

    // Sleduj celý document pre pridanie/odstránenie elementov
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Skús aj po čase (fallback)
    const timeout1 = setTimeout(() => setupAggressiveListeners(), 100);
    const timeout2 = setTimeout(() => setupAggressiveListeners(), 500);
    const timeout3 = setTimeout(() => setupAggressiveListeners(), 1000);

    // Periodické kontrolovanie každých 2 sekúnd (fallback pre prípad, že MutationObserver nezachytí zmeny)
    const interval = setInterval(() => {
      console.log('[REGISTER] 🔄 Periodic check: reinstalling listeners');
      setupAggressiveListeners();
    }, 2000);

    return () => {
      // Odstráň všetky listenery
      cleanupFunctions.forEach(cleanup => cleanup());
      cleanupFunctions = [];
      observer.disconnect();
      clearInterval(interval);
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, [handleRegisterSubmit, registerMutation.isPending]);

  // Debug: skontroluj button po renderovaní a pridaj test funkciu
  useEffect(() => {
    const checkButton = () => {
      const button = document.getElementById('register-button');
      if (button) {
        const btn = button as HTMLButtonElement;
        const styles = window.getComputedStyle(btn);
        console.log('[REGISTER] Button found in DOM:', {
          id: btn.id,
          disabled: btn.disabled,
          type: btn.type,
          className: btn.className,
          pointerEvents: styles.pointerEvents,
          zIndex: styles.zIndex,
          display: styles.display,
          visibility: styles.visibility,
          opacity: styles.opacity,
          position: styles.position,
          top: styles.top,
          left: styles.left,
          width: styles.width,
          height: styles.height
        });
        
        // Skontroluj, či nie je prekrytý iným elementom
        const rect = btn.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const elementAtPoint = document.elementFromPoint(centerX, centerY);
        console.log('[REGISTER] Element at button center:', elementAtPoint);
        console.log('[REGISTER] Is button at center?', elementAtPoint === btn || btn.contains(elementAtPoint));
        
        // Ak je button prekrytý, zistíme, čo ho prekrýva
        if (elementAtPoint && elementAtPoint !== btn && !btn.contains(elementAtPoint)) {
          console.log('[REGISTER] ⚠️ Button is OVERLAID by element:', {
            tagName: elementAtPoint.tagName,
            id: elementAtPoint.id,
            className: elementAtPoint.className,
            zIndex: window.getComputedStyle(elementAtPoint).zIndex,
            pointerEvents: window.getComputedStyle(elementAtPoint).pointerEvents,
            position: window.getComputedStyle(elementAtPoint).position
          });
          
          // Skúsime nastaviť pointer-events: none na overlay elemente
          const overlayStyle = window.getComputedStyle(elementAtPoint);
          if (overlayStyle.pointerEvents !== 'none') {
            console.log('[REGISTER] 🔧 Attempting to fix overlay by setting pointer-events: none');
            (elementAtPoint as HTMLElement).style.pointerEvents = 'none';
          }
        }
        
        // Nastavíme vysoký z-index na button, aby bol navrchu
        if (styles.zIndex === 'auto' || !styles.zIndex || parseInt(styles.zIndex) < 1000) {
          console.log('[REGISTER] 🔧 Setting high z-index on button');
          btn.style.zIndex = '10000';
          btn.style.position = 'relative';
        }
        
        // Odstráň disabled ak je
        if (btn.disabled && !registerMutation.isPending) {
          console.log('[REGISTER] Button is disabled but should not be! Removing disabled...');
          btn.disabled = false;
          btn.removeAttribute('disabled');
        }
        
        // Test: skús manuálne pridať onclick
        btn.onclick = (e) => {
          console.log('[REGISTER] DIRECT onclick attribute fired!');
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          handleRegisterSubmit();
        };
        
        // Test: skús manuálne pridať addEventListener
        const clickHandler = (e: Event) => {
          console.log('[REGISTER] DIRECT addEventListener click fired!');
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          handleRegisterSubmit();
        };
        btn.addEventListener('click', clickHandler, true);
        btn.addEventListener('mousedown', (e) => {
          console.log('[REGISTER] DIRECT addEventListener mousedown fired!');
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          handleRegisterSubmit();
        }, true);
        
        // Pridaj test funkcie na window
        (window as any).testRegister = () => {
          console.log('[REGISTER] TEST: Manually calling handleRegisterSubmit');
          handleRegisterSubmit();
        };
        
        (window as any).testRegisterForm = () => {
          console.log('[REGISTER] TEST: Manually submitting form');
          const form = document.getElementById('register-form') as HTMLFormElement;
          if (form) {
            form.requestSubmit();
          } else {
            console.log('[REGISTER] Form not found!');
          }
        };
        
        (window as any).testRegisterClick = () => {
          console.log('[REGISTER] TEST: Manually clicking button');
          btn.click();
        };
        
        console.log('[REGISTER] Test functions available:');
        console.log('  - window.testRegister() - call handleRegisterSubmit directly');
        console.log('  - window.testRegisterForm() - submit form');
        console.log('  - window.testRegisterClick() - click button');
        
      } else {
        console.log('[REGISTER] Button NOT found in DOM!');
      }
    };
    
    // Skontroluj hneď a po malom čase
    checkButton();
    const timeout = setTimeout(checkButton, 500);
    const timeout2 = setTimeout(checkButton, 1000);
    const timeout3 = setTimeout(checkButton, 2000);
    return () => {
      clearTimeout(timeout);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, [handleRegisterSubmit, registerMutation.isPending]);

  // Debug: loguj validáciu
  useEffect(() => {
    console.log('[REGISTER] Form validation state:', {
      isValid: registerForm.formState.isValid,
      errors: registerForm.formState.errors,
      isDirty: registerForm.formState.isDirty
    });
  }, [registerForm.formState.isValid, registerForm.formState.errors, registerForm.formState.isDirty]);

  useEffect(() => {
    if (user) {
      setLocation("/");
    }
  }, [user, setLocation]);

  // Priamy event listener na document level - obísť extension blokovanie
  useEffect(() => {
    console.log('[REGISTER] Setting up document-level event listeners');
    
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Skontrolujeme, či klik bol na button alebo jeho deti
      if (target && (
        target.closest('[data-register-button]') ||
        target.id === 'register-button' ||
        target.closest('#register-button') ||
        target.textContent?.includes('Vytvoriť účet')
      )) {
        console.log('[REGISTER] DOCUMENT click listener fired on button!', target);
        // Nespúšťame preventDefault, nech form onSubmit funguje
        if (!registerMutation.isPending) {
          // Trigger form submit
          const form = target.closest('form');
          if (form) {
            console.log('[REGISTER] Triggering form submit from document listener');
            form.requestSubmit();
          }
        }
      }
    };

    const handleDocumentMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target && (
        target.closest('[data-register-button]') ||
        target.id === 'register-button' ||
        target.closest('#register-button') ||
        target.textContent?.includes('Vytvoriť účet')
      )) {
        console.log('[REGISTER] DOCUMENT mouseDown listener fired on button!', target);
      }
    };

    // Použijeme capture phase na document level
    document.addEventListener('click', handleDocumentClick, true);
    document.addEventListener('mousedown', handleDocumentMouseDown, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
      document.removeEventListener('mousedown', handleDocumentMouseDown, true);
    };
  }, [registerMutation.isPending]);

  // Štýl pre pozadie, ak je nastavené
  const bgStyle = bgImageUrl 
    ? { 
        backgroundImage: `url(${bgImageUrl})`, 
        backgroundSize: 'cover', 
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat' 
      } 
    : {};
  
  return (
    <div className="min-h-screen flex">
      {/* Ľavá strana - prihlásiaci formulár */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center">
            {getImageValue("app.logo") && (
              <div className="flex justify-center mb-4">
                <img 
                  src={getImageValue("app.logo")} 
                  alt="Logo" 
                  className="h-16 object-contain mb-2" 
                />
              </div>
            )}
            <h1 className="text-2xl font-bold tracking-tight">{appTitle}</h1>
            <p className="text-sm text-muted-foreground">{appSubtitle}</p>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="login">Prihlásenie</TabsTrigger>
                <TabsTrigger value="register">Registrácia</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={loginForm.handleSubmit(data => loginMutation.mutate(data))}>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="login-email">Email</Label>
                      <Input 
                        id="login-email" 
                        type="email"
                        {...loginForm.register("email")} 
                      />
                      {loginForm.formState.errors.email && (
                        <p className="text-sm text-destructive mt-1">{loginForm.formState.errors.email.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="login-password">Heslo</Label>
                      <Input 
                        id="login-password" 
                        type="password" 
                        {...loginForm.register("password")} 
                      />
                      {loginForm.formState.errors.password && (
                        <p className="text-sm text-destructive mt-1">{loginForm.formState.errors.password.message}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="remember-me" 
                        {...loginForm.register("rememberMe")} 
                      />
                      <Label htmlFor="remember-me">Zapamätať prihlásenie</Label>
                    </div>
                    <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
                      {loginMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <FaSignInAlt className="mr-2" /> Prihlásiť sa
                        </>
                      )}
                    </Button>
                    <div className="text-center mt-4">
                      <button 
                        type="button"
                        className="text-sm text-primary hover:underline" 
                        onClick={() => setForgotPasswordOpen(true)}
                      >
                        Zabudli ste heslo?
                      </button>
                    </div>
                    <div className="text-center">
                      <button
                        type="button"
                        className="text-sm text-primary hover:underline"
                        onClick={handleResendVerification}
                      >
                        Preposlať verifikačný email
                      </button>
                    </div>
                  </div>
                </form>
                
                {/* Dialog pre zabudnuté heslo */}
                <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>Obnova zabudnutého hesla</DialogTitle>
                      <DialogDescription>
                        {resetRequestSent 
                          ? "Pokyny na obnovenie hesla boli odoslané na vašu emailovú adresu."
                          : "Zadajte vašu emailovú adresu a my vám pošleme pokyny na obnovenie hesla."}
                      </DialogDescription>
                    </DialogHeader>
                    
                    {!resetRequestSent ? (
                      <form onSubmit={forgotPasswordForm.handleSubmit(handleForgotPassword)}>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                              id="email"
                              type="email"
                              {...forgotPasswordForm.register("email")}
                            />
                            {forgotPasswordForm.formState.errors.email && (
                              <p className="text-sm text-destructive">
                                {forgotPasswordForm.formState.errors.email.message}
                              </p>
                            )}
                          </div>
                        </div>
                        <DialogFooter>
                          <Button type="submit" className="w-full">
                            <FaKey className="mr-2" /> Odoslať pokyny
                          </Button>
                        </DialogFooter>
                      </form>
                    ) : (
                      <div className="py-6 flex justify-center">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => {
                            setForgotPasswordOpen(false);
                            setResetRequestSent(false);
                            forgotPasswordForm.reset();
                          }}
                        >
                          Zavrieť
                        </Button>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </TabsContent>

              <TabsContent value="register">
                <form 
                  id="register-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('[REGISTER] ========== FORM onSubmit EVENT ==========');
                    handleRegisterSubmit();
                  }} 
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      console.log('[REGISTER] Form Enter key pressed');
                      handleRegisterSubmit();
                    }
                  }}
                >
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="register-email">Email</Label>
                      <Input 
                        id="register-email" 
                        type="email" 
                        {...registerForm.register("email")} 
                      />
                      {registerForm.formState.errors.email && (
                        <p className="text-sm text-destructive mt-1">{registerForm.formState.errors.email.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="register-password">Heslo</Label>
                      <Input 
                        id="register-password" 
                        type="password" 
                        autoComplete="new-password"
                        data-form-type="other"
                        {...registerForm.register("password")} 
                      />
                      {registerForm.formState.errors.password && (
                        <p className="text-sm text-destructive mt-1">{registerForm.formState.errors.password.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="register-confirm-password">Potvrdenie hesla</Label>
                      <Input 
                        id="register-confirm-password" 
                        type="password" 
                        autoComplete="new-password"
                        data-form-type="other"
                        {...registerForm.register("confirmPassword")} 
                      />
                      {registerForm.formState.errors.confirmPassword && (
                        <p className="text-sm text-destructive mt-1">{registerForm.formState.errors.confirmPassword.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="register-business-id">IČO</Label>
                      <Input 
                        id="register-business-id" 
                        {...registerForm.register("businessId")} 
                      />
                      {registerForm.formState.errors.businessId && (
                        <p className="text-sm text-destructive mt-1">{registerForm.formState.errors.businessId.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="register-market">Kde sa nachádzate?</Label>
                      <Select 
                        value={registerForm.watch("market") || "SK"}
                        onValueChange={(value) => {
                          registerForm.setValue("market", value as "SK" | "US", { shouldValidate: true });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Vyberte váš trh" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SK">🇸🇰 Slovensko / Európska únia</SelectItem>
                          <SelectItem value="US">🇺🇸 USA / International</SelectItem>
                        </SelectContent>
                      </Select>
                      {registerForm.formState.errors.market && (
                        <p className="text-sm text-destructive mt-1">{registerForm.formState.errors.market.message}</p>
                      )}
                    </div>
                    <Button 
                      ref={registerButtonRef}
                      id="register-button"
                      data-register-button="true"
                      type="submit"
                      className="w-full" 
                      disabled={registerMutation.isPending}
                      style={{ 
                        pointerEvents: 'auto', 
                        width: '100%',
                        cursor: registerMutation.isPending ? 'not-allowed' : 'pointer',
                        position: 'relative',
                        zIndex: 10000
                      }}
                      onMouseDown={(e) => {
                        console.log('[REGISTER] JSX onMouseDown fired!', { isPending: registerMutation.isPending });
                        if (!registerMutation.isPending) {
                          // Nespúšťame preventDefault, nech form onSubmit funguje
                          const form = e.currentTarget.closest('form') as HTMLFormElement;
                          if (form) {
                            console.log('[REGISTER] Triggering form.requestSubmit() from onMouseDown');
                            form.requestSubmit();
                          }
                        }
                      }}
                      onPointerDown={(e) => {
                        console.log('[REGISTER] JSX onPointerDown fired!', { isPending: registerMutation.isPending });
                        if (!registerMutation.isPending) {
                          const form = e.currentTarget.closest('form') as HTMLFormElement;
                          if (form) {
                            console.log('[REGISTER] Triggering form.requestSubmit() from onPointerDown');
                            form.requestSubmit();
                          }
                        }
                      }}
                      onClick={(e) => {
                        console.log('[REGISTER] JSX onClick fired!', { isPending: registerMutation.isPending });
                        if (!registerMutation.isPending) {
                          // onClick sa spúšťa po onMouseDown, takže form už môže byť submitnutý
                          // Ale skúsime znova, ak sa nestalo
                          const form = e.currentTarget.closest('form') as HTMLFormElement;
                          if (form) {
                            console.log('[REGISTER] Triggering form.requestSubmit() from onClick');
                            form.requestSubmit();
                          }
                        }
                      }}
                    >
                      {registerMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <FaUserPlus className="mr-2" /> Vytvoriť účet
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      <div 
        className="hidden lg:flex flex-1 bg-muted items-center justify-center p-12 relative overflow-hidden"
        style={{
          backgroundImage: slides.length > 0 && slides[currentSlideIndex]?.image ? `url(${slides[currentSlideIndex].image})` : bgImageUrl ? `url(${bgImageUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay pre lepšiu čitateľnosť */}
        <div className="absolute inset-0 bg-black/40" />
        
        {/* Slider controls */}
        {slides.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 text-white"
              onClick={() => setCurrentSlideIndex((prev) => (prev - 1 + slides.length) % slides.length)}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 text-white"
              onClick={() => setCurrentSlideIndex((prev) => (prev + 1) % slides.length)}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
            
            {/* Slide indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {slides.map((_, index) => (
                <button
                  key={index}
                  className={`h-2 rounded-full transition-all ${
                    index === currentSlideIndex ? 'bg-white w-8' : 'bg-white/50 w-2'
                  }`}
                  onClick={() => setCurrentSlideIndex(index)}
                />
              ))}
            </div>
          </>
        )}
        
        {/* Content */}
        <div className="max-w-lg text-center relative z-10 text-white">
          {slides.length > 0 && slides[currentSlideIndex] ? (
            <>
              <h2 className="text-3xl font-bold mb-4">{slides[currentSlideIndex].title || welcomeTitle}</h2>
              <p className="text-lg mb-6">
                {slides[currentSlideIndex].description || welcomeDescription}
              </p>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold mb-4">{welcomeTitle}</h2>
              <p className="text-lg text-muted-foreground mb-6">
                {welcomeDescription}
              </p>
              <div className="grid grid-cols-2 gap-4 text-left">
                <div className="p-4 bg-background/80 rounded-lg">
                  <h3 className="font-semibold mb-2">Správa klientov</h3>
                  <p className="text-sm text-muted-foreground">Udržujte prehľad o klientoch a ich hodinových sadzbách</p>
                </div>
                <div className="p-4 bg-background/80 rounded-lg">
                  <h3 className="font-semibold mb-2">Sledovanie úloh</h3>
                  <p className="text-sm text-muted-foreground">Organizujte a prioritizujte svoju prácu efektívne</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}